import { useState } from 'react';
import { Box, Button, Checkbox, Flex, Input, Stack, Text, TextInput } from '@mantine/core';
import { isValidEmail } from '@dealstack/shared/lib/validation';
import {
	StepHeading,
	StepSubHeading,
} from '@dealstack/frontend/shared/components/EidEasySigningModal/steps/typography';
import {
	TransInvestor,
	useTranslationInvestor,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	defaultEmail?: string;
	isSubmitting?: boolean;
	/** Overrides "Continue to sign" where the step nominates a witness without signing. */
	submitLabel?: string;
	onContinue: (email: string) => void;
}

export const WitnessSigningStep = ({
	defaultEmail = '',
	isSubmitting = false,
	submitLabel,
	onContinue,
}: Props) => {
	const tInvestor = useTranslationInvestor();
	const [email, setEmail] = useState(defaultEmail);
	const [eligibility, setEligibility] = useState({
		independent: false,
		ageOfMind: false,
		notParty: false,
	});

	const isValid = isValidEmail(email);
	const allConfirmed = eligibility.independent && eligibility.ageOfMind && eligibility.notParty;
	const checkboxStyles = { body: { alignItems: 'flex-start' } } as const;

	return (
		<Stack p="1rem" gap="1rem" h="100%">
			<Stack p={0} gap="0.5rem">
				<StepHeading>{tInvestor('document_workflow.witness.configure_title')}</StepHeading>
				<StepSubHeading>
					{tInvestor('document_workflow.witness.configure_description')}
				</StepSubHeading>
			</Stack>

			<TextInput
				size="md"
				data-testid={TEST_ELEMENT_IDS.INVESTOR_WITNESS_CONFIG_EMAIL_INPUT}
				label={tInvestor('document_workflow.witness.email_label')}
				placeholder={tInvestor('document_workflow.witness.email_placeholder')}
				value={email}
				onChange={(e) => setEmail(e.currentTarget.value)}
				type="email"
				required
			/>

			<Stack gap="0.5rem">
				<Input.Label required size="lg" sx={(theme) => ({ color: theme.colors.dark[8] })}>
					{tInvestor('document_workflow.witness.eligibility_label')}
				</Input.Label>
				<Box
					p="md"
					sx={(theme) => ({
						borderRadius: theme.radius.sm,
						border: `1px solid ${theme.colors.gray[4]}`,
					})}
				>
					<Stack gap="md">
						<Checkbox
							data-testid={TEST_ELEMENT_IDS.INVESTOR_WITNESS_CONFIG_INDEPENDENT}
							checked={eligibility.independent}
							onChange={(e) => {
								const { checked } = e.currentTarget;
								setEligibility((prev) => ({ ...prev, independent: checked }));
							}}
							styles={checkboxStyles}
							label={
								<TransInvestor i18nKey="document_workflow.witness.eligibility_independent">
									They are{' '}
									<Text span fw={700}>
										independent
									</Text>{' '}
									— not my spouse, civil partner, or close family.
								</TransInvestor>
							}
						/>
						<Checkbox
							data-testid={TEST_ELEMENT_IDS.INVESTOR_WITNESS_CONFIG_AGE}
							checked={eligibility.ageOfMind}
							onChange={(e) => {
								const { checked } = e.currentTarget;
								setEligibility((prev) => ({ ...prev, ageOfMind: checked }));
							}}
							styles={checkboxStyles}
							label={
								<TransInvestor i18nKey="document_workflow.witness.eligibility_age">
									They are{' '}
									<Text span fw={700}>
										aged 18 or over
									</Text>{' '}
									and of sound mind.
								</TransInvestor>
							}
						/>
						<Checkbox
							data-testid={TEST_ELEMENT_IDS.INVESTOR_WITNESS_CONFIG_NOT_PARTY}
							checked={eligibility.notParty}
							onChange={(e) => {
								const { checked } = e.currentTarget;
								setEligibility((prev) => ({ ...prev, notParty: checked }));
							}}
							styles={checkboxStyles}
							label={
								<TransInvestor i18nKey="document_workflow.witness.eligibility_not_party">
									They are{' '}
									<Text span fw={700}>
										not a party
									</Text>{' '}
									to this document.
								</TransInvestor>
							}
						/>
					</Stack>
				</Box>
			</Stack>

			<Flex direction="column" mt="auto">
				<Button
					w="100%"
					data-testid={TEST_ELEMENT_IDS.INVESTOR_WITNESS_CONFIG_CONTINUE_BUTTON}
					disabled={!isValid || !allConfirmed || isSubmitting}
					loading={isSubmitting}
					onClick={() => onContinue(email.trim())}
				>
					{submitLabel ?? tInvestor('document_workflow.witness.continue_to_sign')}
				</Button>
			</Flex>
		</Stack>
	);
};
